// src/lib/svg/eraserUtils.js Eraser tool geometry: polygon construction, bezier-preserving erasure, coordinate transforms.

import polygonClipping from 'polygon-clipping'
import {
	booleanSubtract,
	evaluateBezier,
	pointInBezierPath,
	resolveContours,
	splitBezierAtMultiple,
} from './booleanOps'
import { buildOutlineFromBezier, offsetCubicAdaptive, sampleCenterline, schneiderFit } from './brushUtils'
import { pointsToSegment, rawPathToString, resetPathOrigin, stringToRawPath } from './paths'

// --- Helpers ---

/** Shoelace formula for polygon area (signed). */
function signedArea(ring) {
	let area = 0
	for (let i = 0, n = ring.length; i < n; i++) {
		const j = (i + 1) % n
		area += ring[i][0] * ring[j][1]
		area -= ring[j][0] * ring[i][1]
	}
	return area / 2
}

export function polygonArea(ring) {
	return Math.abs(signedArea(ring))
}

/** Bidirectional EMA smoothing on flat [x,y,...] pairs. Smoothness 0–100 controls alpha. */
function smoothEraserPath(pairs, smoothness = 50) {
	const n = pairs.length / 2
	if (n < 3) return pairs
	const alpha = 1 - (smoothness / 100) * 0.95
	// Forward pass
	const fwd = new Float64Array(pairs.length)
	fwd[0] = pairs[0]
	fwd[1] = pairs[1]
	for (let i = 1; i < n; i++) {
		fwd[i * 2] = alpha * pairs[i * 2] + (1 - alpha) * fwd[(i - 1) * 2]
		fwd[i * 2 + 1] = alpha * pairs[i * 2 + 1] + (1 - alpha) * fwd[(i - 1) * 2 + 1]
	}
	// Backward pass
	const bwd = new Float64Array(pairs.length)
	bwd[(n - 1) * 2] = pairs[(n - 1) * 2]
	bwd[(n - 1) * 2 + 1] = pairs[(n - 1) * 2 + 1]
	for (let i = n - 2; i >= 0; i--) {
		bwd[i * 2] = alpha * pairs[i * 2] + (1 - alpha) * bwd[(i + 1) * 2]
		bwd[i * 2 + 1] = alpha * pairs[i * 2 + 1] + (1 - alpha) * bwd[(i + 1) * 2 + 1]
	}
	// Average
	const out = new Array(pairs.length)
	out[0] = pairs[0]
	out[1] = pairs[1]
	out[(n - 1) * 2] = pairs[(n - 1) * 2]
	out[(n - 1) * 2 + 1] = pairs[(n - 1) * 2 + 1]
	for (let i = 1; i < n - 1; i++) {
		out[i * 2] = (fwd[i * 2] + bwd[i * 2]) / 2
		out[i * 2 + 1] = (fwd[i * 2 + 1] + bwd[i * 2 + 1]) / 2
	}
	return out
}

/**
 * Even-odd point-in-polygon test using ray casting. polygon is [[x,y], ...] ring (closed or open).
 */
function pointInPolygon(px, py, polygon) {
	let inside = false
	for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
		const xi = polygon[i][0],
			yi = polygon[i][1]
		const xj = polygon[j][0],
			yj = polygon[j][1]
		if (yi > py !== yj > py && px < ((xj - xi) * (py - yi)) / (yj - yi) + xi) {
			inside = !inside
		}
	}
	return inside
}

/** Ramer-Douglas-Peucker polyline simplification. points = [[x,y], ...]. */
function simplifyPoints(points, tolerance) {
	if (points.length <= 2) return points
	let maxDist = 0,
		maxIdx = 0
	const [ax, ay] = points[0],
		[bx, by] = points[points.length - 1]
	const dx = bx - ax,
		dy = by - ay
	const lenSq = dx * dx + dy * dy
	for (let i = 1; i < points.length - 1; i++) {
		const [px, py] = points[i]
		const dist =
			lenSq > 0
				? Math.abs(dy * px - dx * py + bx * ay - by * ax) / Math.sqrt(lenSq)
				: Math.hypot(px - ax, py - ay)
		if (dist > maxDist) {
			maxDist = dist
			maxIdx = i
		}
	}
	if (maxDist > tolerance) {
		const left = simplifyPoints(points.slice(0, maxIdx + 1), tolerance)
		const right = simplifyPoints(points.slice(maxIdx), tolerance)
		return [...left.slice(0, -1), ...right]
	}
	return [points[0], points[points.length - 1]]
}

// --- Core geometry ---

/**
 * Build eraser shape as a smooth bezier closed contour from centerline + fixed width. Returns curve8[][] (array of closed bezier contours — typically just one).
 *
 * Approach: "stroke to path" — compute left/right offset from centerline normals, add round caps, fit smooth bezier curves through the outline via pointsToSegment.
 */
export function buildEraserCurves(points, radius, smoothness = 50) {
	const smoothed = smoothEraserPath(points, smoothness)
	const n = smoothed.length / 2
	if (n < 2) {
		return [circleAsCurves(smoothed[0], smoothed[1], radius)]
	}

	const segment = schneiderFit(smoothed, smoothness)
	if (!segment || segment.length < 8) return [circleAsCurves(smoothed[0], smoothed[1], radius)]

	// Offset each centerline cubic at constant radius using per-cubic Tiller-Hanson offset. This preserves consistent winding even when the centerline backtracks.
	const allLeft = []
	const allRight = []
	for (let i = 2; i < segment.length; i += 6) {
		const x0 = i === 2 ? segment[0] : segment[i - 2]
		const y0 = i === 2 ? segment[1] : segment[i - 1]
		const curve = [
			x0,
			y0,
			segment[i],
			segment[i + 1],
			segment[i + 2],
			segment[i + 3],
			segment[i + 4],
			segment[i + 5],
		]
		const { left, right } = offsetCubicAdaptive(curve, radius)
		allLeft.push(...left)
		allRight.push(...right)
	}
	if (!allLeft.length) return [circleAsCurves(smoothed[0], smoothed[1], radius)]

	// Assemble: left forward + end cap + right backward + start cap End cap: semicircle from last-left endpoint to last-right endpoint
	const lastLeft = allLeft[allLeft.length - 1]
	const lastRight = allRight[allRight.length - 1]
	const endCapCx = (lastLeft[6] + lastRight[6]) / 2
	const endCapCy = (lastLeft[7] + lastRight[7]) / 2
	const endCap = semicircleArc(lastLeft[6], lastLeft[7], lastRight[6], lastRight[7], endCapCx, endCapCy)

	// Start cap: semicircle from first-right endpoint to first-left endpoint
	const firstLeft = allLeft[0]
	const firstRight = allRight[0]
	const startCapCx = (firstLeft[0] + firstRight[0]) / 2
	const startCapCy = (firstLeft[1] + firstRight[1]) / 2
	const startCap = semicircleArc(firstRight[0], firstRight[1], firstLeft[0], firstLeft[1], startCapCx, startCapCy)

	// Build flat curve8[] list
	const curves = [
		...allLeft,
		...endCap,
		...[...allRight].reverse().map(c => [c[6], c[7], c[4], c[5], c[2], c[3], c[0], c[1]]),
		...startCap,
	].filter(c => Math.hypot(c[6] - c[0], c[7] - c[1]) > 1e-6)

	return [curves]
}

/**
 * Build a semicircle arc from p0 to p1 around center (cx,cy) as 2 cubic bezier curves. Uses the kappa constant for 90° circular arc approximation. p0 and p1 are on the circle; the arc goes from p0 to apex to p1 (the far side from chord).
 */
function semicircleArc(x0, y0, x1, y1, cx, cy) {
	const K = 0.5522847498
	// Apex: midpoint of the arc (perpendicular to chord, on the far side from center relative to chord) For a semicircle where p0,p1 are diametrically opposite, apex is perpendicular to p0-p1 through center
	const dx = x1 - x0,
		dy = y1 - y0
	// Normal to the chord, pointing outward (away from the chord midpoint toward the arc) The arc goes on the side where the center is NOT relative to the chord midpoint Since p0,p1 are on the circle, apex = center + perpendicular direction * radius
	const nx = -dy,
		ny = dx // perpendicular to chord
	const len = Math.hypot(nx, ny) || 1
	// Determine which direction: apex should be on the opposite side of the chord from the interior For the eraser cap, we go outward from the stroke body
	const r = Math.hypot(x0 - cx, y0 - cy)
	const mx = cx + (nx / len) * r
	const my = cy + (ny / len) * r

	// Tangent at each point is perpendicular to the radius at that point (CW direction for p0→apex→p1) At p0: tangent direction = rotate (p0-center) by 90° CW = (-(y0-cy), (x0-cx)) normalized * r... no, just the direction
	const t0x = y0 - cy,
		t0y = -(x0 - cx) // tangent at p0 (CW around center)
	const tmx = my - cy,
		tmy = -(mx - cx) // tangent at apex
	const t1x = y1 - cy,
		t1y = -(x1 - cx) // tangent at p1

	// p0 → apex: 90° arc
	const c1 = [x0, y0, x0 + t0x * K, y0 + t0y * K, mx - tmx * K, my - tmy * K, mx, my]
	// apex → p1: 90° arc
	const c2 = [mx, my, mx + tmx * K, my + tmy * K, x1 - t1x * K, y1 - t1y * K, x1, y1]
	return [c1, c2]
}

/** Build a circle as smooth bezier curves (4 cubic arcs). */
function circleAsCurves(cx, cy, r) {
	// Standard 4-arc circle approximation: kappa = 4*(sqrt(2)-1)/3
	const k = r * 0.5522847498
	return [
		[cx + r, cy, cx + r, cy + k, cx + k, cy + r, cx, cy + r],
		[cx, cy + r, cx - k, cy + r, cx - r, cy + k, cx - r, cy],
		[cx - r, cy, cx - r, cy - k, cx - k, cy - r, cx, cy - r],
		[cx, cy - r, cx + k, cy - r, cx + r, cy - k, cx + r, cy],
	]
}

/**
 * Flatten a rawPath (array of bezier segments) to a polygon [[x,y], ...]. Samples each cubic bezier at approximately `interval` px.
 */
export function flattenBezierPath(rawPath, interval = 4) {
	const points = []

	for (let s = 0; s < rawPath.length; s++) {
		const seg = rawPath[s]
		if (seg.length < 2) continue
		points.push([seg[0], seg[1]])

		for (let i = 2; i < seg.length; i += 6) {
			const x0 = seg[i - 2],
				y0 = seg[i - 1]
			const cp1x = seg[i],
				cp1y = seg[i + 1]
			const cp2x = seg[i + 2],
				cp2y = seg[i + 3]
			const x1 = seg[i + 4],
				y1 = seg[i + 5]

			// Estimate curve length for subdivision count
			const chordLen = Math.hypot(x1 - x0, y1 - y0)
			const controlLen =
				Math.hypot(cp1x - x0, cp1y - y0) +
				Math.hypot(cp2x - cp1x, cp2y - cp1y) +
				Math.hypot(x1 - cp2x, y1 - cp2y)
			const approxLen = (chordLen + controlLen) / 2
			const steps = Math.max(2, Math.ceil(approxLen / interval))

			for (let j = 1; j <= steps; j++) {
				const t = j / steps
				const u = 1 - t
				const x = u * u * u * x0 + 3 * u * u * t * cp1x + 3 * u * t * t * cp2x + t * t * t * x1
				const y = u * u * u * y0 + 3 * u * u * t * cp1y + 3 * u * t * t * cp2y + t * t * t * y1
				points.push([x, y])
			}
		}
	}

	// Close if the path is closed
	if (points.length > 2) {
		const first = points[0],
			last = points[points.length - 1]
		if (Math.abs(first[0] - last[0]) < 0.5 && Math.abs(first[1] - last[1]) < 0.5) {
			points[points.length - 1] = [first[0], first[1]]
		} else {
			points.push([first[0], first[1]])
		}
	}

	return points
}

/**
 * Transform eraser curves from world space to object-local space. eraserCurves: curve8[][] (array of contours, each contour is curve8[]). Object has x, y, rotation (degrees), scaleX, scaleY.
 */
export function worldToLocal(eraserCurves, obj) {
	const ox = obj.x || 0
	const oy = obj.y || 0
	const rot = (-(obj.rotation || 0) * Math.PI) / 180
	const sx = 1 / (obj.scaleX ?? 1)
	const sy = 1 / (obj.scaleY ?? 1)
	const cosR = Math.cos(rot)
	const sinR = Math.sin(rot)

	function xform(wx, wy) {
		const dx = wx - ox
		const dy = wy - oy
		const rx = dx * cosR - dy * sinR
		const ry = dx * sinR + dy * cosR
		return [rx * sx, ry * sy]
	}

	return eraserCurves.map(contour =>
		contour.map(c => {
			const [x0, y0] = xform(c[0], c[1])
			const [c1x, c1y] = xform(c[2], c[3])
			const [c2x, c2y] = xform(c[4], c[5])
			const [x1, y1] = xform(c[6], c[7])
			return [x0, y0, c1x, c1y, c2x, c2y, x1, y1]
		})
	)
}

// --- Bezier-preserving eraser helpers ---

/**
 * Solve cubic polynomial at³ + bt² + ct + d = 0 for real roots in [0, 1]. Returns array of roots (may be empty).
 */
function solveCubicRoots(a, b, c, d) {
	const EPS = 1e-8
	const roots = []

	const addRoot = t => {
		if (t >= -EPS && t <= 1 + EPS) {
			roots.push(Math.max(0, Math.min(1, t)))
		}
	}

	// Degenerate: linear
	if (Math.abs(a) < EPS && Math.abs(b) < EPS) {
		if (Math.abs(c) > EPS) addRoot(-d / c)
		return roots
	}

	// Degenerate: quadratic
	if (Math.abs(a) < EPS) {
		const disc = c * c - 4 * b * d
		if (disc < 0) return roots
		const sq = Math.sqrt(disc)
		addRoot((-c + sq) / (2 * b))
		addRoot((-c - sq) / (2 * b))
		return roots
	}

	// Cubic: use depressed cubic (Cardano / trig)
	const p = (3 * a * c - b * b) / (3 * a * a)
	const q = (2 * b * b * b - 9 * a * b * c + 27 * a * a * d) / (27 * a * a * a)
	const disc = (q * q) / 4 + (p * p * p) / 27
	const shift = -b / (3 * a)

	if (disc > EPS) {
		// One real root
		const sqrtDisc = Math.sqrt(disc)
		const u = Math.cbrt(-q / 2 + sqrtDisc)
		const v = Math.cbrt(-q / 2 - sqrtDisc)
		addRoot(u + v + shift)
	} else if (disc < -EPS) {
		// Three real roots (casus irreducibilis)
		const r = Math.sqrt((-p * p * p) / 27)
		const theta = Math.acos(Math.max(-1, Math.min(1, -q / (2 * r))))
		const cube = 2 * Math.cbrt(r)
		addRoot(cube * Math.cos(theta / 3) + shift)
		addRoot(cube * Math.cos((theta + 2 * Math.PI) / 3) + shift)
		addRoot(cube * Math.cos((theta + 4 * Math.PI) / 3) + shift)
	} else {
		// Double/triple root
		if (Math.abs(q) < EPS) {
			addRoot(shift)
		} else {
			const u = Math.cbrt(-q / 2)
			addRoot(2 * u + shift)
			addRoot(-u + shift)
		}
	}

	return roots
}

/**
 * Find t values where line segment (lx0,ly0)→(lx1,ly1) intersects cubic bezier curve8. Returns array of t values (on the bezier).
 */
function lineBezierIntersections(lx0, ly0, lx1, ly1, curve) {
	// Line implicit form: nx*(x-lx0) + ny*(y-ly0) = 0
	const nx = ly1 - ly0
	const ny = -(lx1 - lx0)

	// Signed distances of bezier control points from line
	const d0 = nx * (curve[0] - lx0) + ny * (curve[1] - ly0)
	const d1 = nx * (curve[2] - lx0) + ny * (curve[3] - ly0)
	const d2 = nx * (curve[4] - lx0) + ny * (curve[5] - ly0)
	const d3 = nx * (curve[6] - lx0) + ny * (curve[7] - ly0)

	// Cubic in power basis from Bernstein coefficients
	const a = -d0 + 3 * d1 - 3 * d2 + d3
	const b = 3 * d0 - 6 * d1 + 3 * d2
	const c = -3 * d0 + 3 * d1
	const d_coef = d0

	const tValues = solveCubicRoots(a, b, c, d_coef)

	// Filter: intersection point must lie within the line segment
	const results = []
	const segLenSq = (lx1 - lx0) ** 2 + (ly1 - ly0) ** 2
	if (segLenSq < 1e-12) return results

	for (const t of tValues) {
		const [px, py] = evaluateBezier(curve, t)
		// Project onto line segment
		const s = ((px - lx0) * (lx1 - lx0) + (py - ly0) * (ly1 - ly0)) / segLenSq
		if (s >= -1e-6 && s <= 1 + 1e-6) {
			results.push(t)
		}
	}
	return results
}

/** Extract all cubic bezier curves from a rawPath segment as curve8 arrays. */
export function extractCurves(segment) {
	const curves = []
	for (let i = 2; i < segment.length; i += 6) {
		curves.push([
			segment[i - 2],
			segment[i - 1],
			segment[i],
			segment[i + 1],
			segment[i + 2],
			segment[i + 3],
			segment[i + 4],
			segment[i + 5],
		])
	}
	return curves
}

/** Assemble array of curve8 tuples into a rawPath segment flat array. */
export function curvesToSegment(curves) {
	if (!curves.length) return []
	const seg = [curves[0][0], curves[0][1]]
	for (const c of curves) {
		seg.push(c[2], c[3], c[4], c[5], c[6], c[7])
	}
	const len = seg.length
	if (len >= 8 && Math.abs(seg[0] - seg[len - 2]) < 0.5 && Math.abs(seg[1] - seg[len - 1]) < 0.5) {
		seg.closed = true
	}
	return seg
}

/**
 * Find which edge of a polygon ring a point lies on. Returns { edgeIndex, param } where param is 0–1 along that edge.
 */
function findEdgePosition(px, py, ring) {
	let bestDist = Infinity
	let bestEdge = 0
	let bestParam = 0
	const n = ring.length - 1 // last point == first point for closed rings

	for (let i = 0; i < n; i++) {
		const ax = ring[i][0],
			ay = ring[i][1]
		const bx = ring[i + 1][0],
			by = ring[i + 1][1]
		const dx = bx - ax,
			dy = by - ay
		const lenSq = dx * dx + dy * dy
		if (lenSq < 1e-12) continue

		let t = ((px - ax) * dx + (py - ay) * dy) / lenSq
		t = Math.max(0, Math.min(1, t))
		const cx = ax + t * dx,
			cy = ay + t * dy
		const dist = (px - cx) ** 2 + (py - cy) ** 2
		if (dist < bestDist) {
			bestDist = dist
			bestEdge = i
			bestParam = t
		}
	}
	return { edgeIndex: bestEdge, param: bestParam }
}

/**
 * Trace along the eraser polygon boundary between two positions in a given direction. direction: 1 = forward (increasing edge index), -1 = backward. Returns [[x,y], ...] vertices including interpolated start/end points.
 */
function traceBoundaryDir(ring, fromPos, toPos, direction) {
	const n = ring.length - 1 // closed ring: ring[n] == ring[0]
	const points = []

	// Interpolated start point
	const fe = fromPos.edgeIndex
	const startPt = [
		ring[fe][0] + fromPos.param * (ring[fe + 1][0] - ring[fe][0]),
		ring[fe][1] + fromPos.param * (ring[fe + 1][1] - ring[fe][1]),
	]
	points.push(startPt)

	if (direction === 1) {
		// Walk forward
		let i = fromPos.param < 1 - 1e-9 ? (fromPos.edgeIndex + 1) % n : fromPos.edgeIndex
		const limit = n + 2
		for (let count = 0; count < limit; count++) {
			if (i === toPos.edgeIndex) break
			points.push([ring[i][0], ring[i][1]])
			i = (i + 1) % n
		}
		if (toPos.param > 1e-9) {
			points.push([ring[toPos.edgeIndex][0], ring[toPos.edgeIndex][1]])
		}
	} else {
		// Walk backward
		let i = fromPos.edgeIndex
		const limit = n + 2
		for (let count = 0; count < limit; count++) {
			if (i === toPos.edgeIndex) break
			points.push([ring[i][0], ring[i][1]])
			i = (i - 1 + n) % n
		}
		if (toPos.param < 1 - 1e-9) {
			const te = toPos.edgeIndex
			points.push([ring[te + 1][0], ring[te + 1][1]])
		}
	}

	// Interpolated end point
	const te = toPos.edgeIndex
	const endPt = [
		ring[te][0] + toPos.param * (ring[te + 1][0] - ring[te][0]),
		ring[te][1] + toPos.param * (ring[te + 1][1] - ring[te][1]),
	]
	points.push(endPt)

	return points
}

/**
 * Compute the area of a closed path made of curve8 sub-curves by sampling each curve and using the shoelace formula.
 */
function curveRunArea(curves) {
	const pts = []
	for (const c of curves) {
		for (let t = 0; t <= 1; t += 0.1) {
			pts.push(evaluateBezier(c, t))
		}
	}
	let area = 0
	for (let i = 0, n = pts.length; i < n; i++) {
		const j = (i + 1) % n
		area += pts[i][0] * pts[j][1]
		area -= pts[j][0] * pts[i][1]
	}
	return Math.abs(area / 2)
}

// --- Erase operations ---

const MIN_FRAGMENT_AREA = 25

/**
 * Erase from a closed vector shape (rect, circle, path, legacy brush). Preserves original bezier control points — only splits curves at eraser boundary crossings. eraserPoly must be in object-local space. Returns array of {data, x, y, width, height} for surviving pieces, or null if no intersection.
 */
export function eraseFromShape(obj, localEraserCurves) {
	if (!localEraserCurves || !localEraserCurves.length) return null

	// Process each eraser contour iteratively — each contour may split pieces further
	let currentPieces = [{ data: obj.data, x: obj.x || 0, y: obj.y || 0 }]
	let anyChanged = false

	for (const contour of localEraserCurves) {
		if (!contour || contour.length < 1) continue
		const nextPieces = []
		for (const piece of currentPieces) {
			const result = eraseShapeWithContour(piece, contour)
			if (result === null) {
				nextPieces.push(piece)
			} else if (result.length === 0) {
				anyChanged = true
			} else {
				anyChanged = true
				for (const r of result) nextPieces.push(r)
			}
		}
		currentPieces = nextPieces
	}

	if (!anyChanged) return null
	if (!currentPieces.length) return []

	// Compute width/height for final pieces
	return currentPieces.map(piece => {
		const rp = stringToRawPath(piece.data)
		let minX = Infinity,
			minY = Infinity,
			maxX = -Infinity,
			maxY = -Infinity
		for (const seg of rp) {
			for (let i = 0; i < seg.length; i += 2) {
				if (seg[i] < minX) minX = seg[i]
				if (seg[i + 1] < minY) minY = seg[i + 1]
				if (seg[i] > maxX) maxX = seg[i]
				if (seg[i + 1] > maxY) maxY = seg[i + 1]
			}
		}
		return { data: piece.data, x: piece.x, y: piece.y, width: maxX - minX, height: maxY - minY }
	})
}

/**
 * Erases from a single segment's curves using the bezier-preserving approach. Finds intersections with eraser ring, splits curves at crossings, classifies sub-curves as inside/outside, groups surviving runs, and closes them along the eraser boundary for closed segments.
 *
 * Returns { runs: curve8[][], anyInside, anyOutside }
 *   - runs: array of surviving curve runs (each run is an array of curve8)
 *   - For closed segments, each run is closed along the eraser boundary.
 */
function eraseSegmentCurves(curves, eraserRing, isClosed) {
	const edgeCount = eraserRing.length - 1
	const curveIntersections = curves.map(() => [])

	for (let ci = 0; ci < curves.length; ci++) {
		const curve = curves[ci]
		for (let ei = 0; ei < edgeCount; ei++) {
			const tVals = lineBezierIntersections(
				eraserRing[ei][0],
				eraserRing[ei][1],
				eraserRing[ei + 1][0],
				eraserRing[ei + 1][1],
				curve
			)
			for (const t of tVals) {
				curveIntersections[ci].push(t)
			}
		}
		// Deduplicate close t values
		if (curveIntersections[ci].length > 1) {
			curveIntersections[ci].sort((a, b) => a - b)
			const deduped = [curveIntersections[ci][0]]
			for (let i = 1; i < curveIntersections[ci].length; i++) {
				if (curveIntersections[ci][i] - deduped[deduped.length - 1] > 1e-6) {
					deduped.push(curveIntersections[ci][i])
				}
			}
			curveIntersections[ci] = deduped
		}
	}

	// Split curves at intersections
	const subCurves = []
	for (let ci = 0; ci < curves.length; ci++) {
		const pieces = splitBezierAtMultiple(curves[ci], curveIntersections[ci])
		for (const p of pieces) {
			subCurves.push(p)
		}
	}

	// Classify sub-curves
	let anyInside = false,
		anyOutside = false
	const outside = subCurves.map(c => {
		const mid = evaluateBezier(c, 0.5)
		const inside = pointInPolygon(mid[0], mid[1], eraserRing)
		if (inside) anyInside = true
		else anyOutside = true
		return !inside
	})

	if (!anyInside || !anyOutside) {
		return { runs: [], anyInside, anyOutside }
	}

	// Group into surviving runs
	const runs = []
	let currentRun = null

	for (let i = 0; i < subCurves.length; i++) {
		if (outside[i]) {
			if (!currentRun) currentRun = []
			currentRun.push(subCurves[i])
		} else {
			if (currentRun && currentRun.length > 0) {
				runs.push(currentRun)
			}
			currentRun = null
		}
	}
	if (currentRun && currentRun.length > 0) {
		runs.push(currentRun)
	}

	// For closed paths, merge first and last runs if they wrap around
	if (isClosed && runs.length >= 2) {
		const firstOutside = outside[0]
		const lastOutside = outside[outside.length - 1]
		if (firstOutside && lastOutside) {
			const lastRun = runs.pop()
			runs[0] = [...lastRun, ...runs[0]]
		}
	}

	// Close each run along eraser boundary (for closed segments)
	if (isClosed) {
		// Build flattened polygon of original shape for containment testing. The correct closing direction traces the eraser boundary through the region that was inside the shape (the removed material).
		const shapePoly = flattenBezierPath([curvesToSegment(curves)])

		for (let ri = 0; ri < runs.length; ri++) {
			const run = runs[ri]
			const runStartPt = [run[0][0], run[0][1]]
			const runEndPt = [run[run.length - 1][6], run[run.length - 1][7]]

			const endPos = findEdgePosition(runEndPt[0], runEndPt[1], eraserRing)
			const startPos = findEdgePosition(runStartPt[0], runStartPt[1], eraserRing)

			// Try both directions; pick the one whose midpoint is inside the shape
			let bestClosing = null
			let bestScore = -Infinity
			for (const dir of [1, -1]) {
				const boundaryPts = traceBoundaryDir(eraserRing, endPos, startPos, dir)
				if (boundaryPts.length < 2) continue
				const simplified = simplifyPoints(boundaryPts, 2)
				const flat = []
				for (const pt of simplified) flat.push(pt[0], pt[1])
				const boundarySeg = pointsToSegment(flat, 0.15)
				const closing = extractCurves(boundarySeg)

				// Test midpoint of closing path for containment in original shape
				const midIdx = Math.floor(boundaryPts.length / 2)
				const midPt = boundaryPts[midIdx]
				const insideShape = pointInPolygon(midPt[0], midPt[1], shapePoly)

				// Also compute signed area for tiebreaking
				const testCurves = [...run, ...closing]
				const testPoly = flattenBezierPath([curvesToSegment(testCurves)])
				let sArea = 0
				for (let pi = 0, pn = testPoly.length; pi < pn; pi++) {
					const pj = (pi + 1) % pn
					sArea += testPoly[pi][0] * testPoly[pj][1] - testPoly[pj][0] * testPoly[pi][1]
				}

				// Score: prefer inside-shape, then CCW, then smaller area
				let score = 0
				if (insideShape) score += 1000
				if (sArea > 0) score += 100
				score -= Math.abs(sArea) * 0.001 // prefer smaller
				if (score > bestScore) {
					bestScore = score
					bestClosing = closing
				}
			}
			if (bestClosing) {
				runs[ri] = [...run, ...bestClosing]
			}
		}
	}

	// Ensure all closed runs have CCW winding (matching outer shape convention)
	if (isClosed) {
		for (let ri = 0; ri < runs.length; ri++) {
			const poly = flattenBezierPath([curvesToSegment(runs[ri])])
			let sArea = 0
			for (let pi = 0, pn = poly.length; pi < pn; pi++) {
				const pj = (pi + 1) % pn
				sArea += poly[pi][0] * poly[pj][1] - poly[pj][0] * poly[pi][1]
			}
			if (sArea < 0) {
				// CW winding — reverse curves to make CCW
				runs[ri] = runs[ri].map(c => [c[6], c[7], c[4], c[5], c[2], c[3], c[0], c[1]]).reverse()
			}
		}
	}

	return { runs, anyInside, anyOutside }
}

/**
 * Detect if a segment is closed (last anchor ≈ first anchor).
 */
function isSegmentClosed(curves) {
	if (!curves.length) return false
	const first = curves[0]
	const last = curves[curves.length - 1]
	return Math.abs(first[0] - last[6]) < 0.5 && Math.abs(first[1] - last[7]) < 0.5
}

/**
 * Collapse runs of consecutive linear (straight-line) curves in a contour into single straight lines. This cleans up eraser boundary segments that trace the polygon edges, replacing them with direct cuts.
 */
function simplifyContourLines(contour) {
	const isLine = c => {
		const dx = c[6] - c[0],
			dy = c[7] - c[1]
		const len = Math.sqrt(dx * dx + dy * dy)
		if (len < 1e-10) return true
		const nx = -dy / len,
			ny = dx / len
		const d1 = Math.abs(nx * (c[2] - c[0]) + ny * (c[3] - c[1]))
		const d2 = Math.abs(nx * (c[4] - c[0]) + ny * (c[5] - c[1]))
		return d1 < 0.01 && d2 < 0.01
	}
	const makeLine = (x0, y0, x1, y1) => [
		x0,
		y0,
		x0 + (x1 - x0) / 3,
		y0 + (y1 - y0) / 3,
		x0 + (2 * (x1 - x0)) / 3,
		y0 + (2 * (y1 - y0)) / 3,
		x1,
		y1,
	]
	// Pass 1: merge collinear line runs into single lines
	const pass1 = []
	let i = 0
	while (i < contour.length) {
		if (!isLine(contour[i])) {
			pass1.push(contour[i])
			i++
		} else {
			// Start of a linear run — merge only collinear consecutive lines
			let j = i + 1
			while (j < contour.length && isLine(contour[j])) {
				const x0 = contour[i][0],
					y0 = contour[i][1]
				const x1 = contour[j][6],
					y1 = contour[j][7]
				const dx = x1 - x0,
					dy = y1 - y0
				const len = Math.sqrt(dx * dx + dy * dy)
				if (len < 1e-6) {
					j++
					continue
				}
				const nx = -dy / len,
					ny = dx / len
				let collinear = true
				for (let k = i; k < j; k++) {
					const d = Math.abs(nx * (contour[k][6] - x0) + ny * (contour[k][7] - y0))
					if (d > 1.0) {
						collinear = false
						break
					}
				}
				if (!collinear) break
				j++
			}
			if (j > i + 1) {
				const last = contour[j - 1]
				pass1.push(makeLine(contour[i][0], contour[i][1], last[6], last[7]))
			} else {
				pass1.push(contour[i])
			}
			i = j
		}
	}

	return pass1
}

/**
 * Erase from a shape using a single eraser contour (curve8[]). Uses bezier-preserving boolean subtraction. piece = { data, x, y }. eraserCurves in object-local space. Returns array of {data, x, y} | null (no intersection) | [] (fully erased).
 */
function eraseShapeWithContour(piece, eraserCurves) {
	const rawPath = stringToRawPath(piece.data)
	if (!rawPath || !rawPath.length) return null

	const segCurves = rawPath.map(seg => extractCurves(seg))
	if (!segCurves.length || !segCurves[0].length) return null

	const outerCurves = segCurves[0]
	const outerClosed = isSegmentClosed(outerCurves)

	if (!outerClosed) {
		// Open paths: use run-based approach (eraseSegmentCurves) Flatten eraser curves to polygon for line-intersection tests
		const eraserRing = flattenBezierPath([curvesToSegment(eraserCurves)])
		const result = eraseSegmentCurves(outerCurves, eraserRing, false)
		if (!result.anyInside) return null
		if (!result.anyOutside) return []
		const pieces = []
		for (const run of result.runs) {
			const pathStr = rawPathToString([curvesToSegment(run)])
			const r = resetPathOrigin(pathStr)
			pieces.push({ data: r.data, x: piece.x + r.x, y: piece.y + r.y })
		}
		return pieces.length ? pieces : []
	}

	// Resolve self-intersections in the eraser ribbon into a clean compound shape. buildEraserCurves produces a correctly-wound ribbon (per-cubic Tiller-Hanson offset) so resolveContours with nonzero winding correctly merges overlapping regions into a clean blob, preserving genuine holes (e.g. interior of a circular stroke).
	const resolvedEraser = resolveContours([eraserCurves])
	const eraserForSubtract = resolvedEraser.length > 0 ? resolvedEraser : eraserCurves

	// Boolean subtract: shape - eraser
	const contours = booleanSubtract(segCurves, eraserForSubtract)

	if (!contours.length) return []

	// Detect disjoint (no change): same number of contours and matching total area
	if (contours.length === segCurves.length) {
		let inputArea = 0,
			outputArea = 0
		for (const c of segCurves) inputArea += Math.abs(curveRunArea(c))
		for (const c of contours) outputArea += Math.abs(curveRunArea(c))
		if (Math.abs(inputArea - outputArea) < 1) return null
	}

	// Simplify contours: collapse runs of consecutive line segments into single lines. The eraser polygon becomes degenerate cubics that trace the eraser boundary;
	// replacing them with a single straight cut reduces anchor point clutter.
	for (let ci = 0; ci < contours.length; ci++) {
		contours[ci] = simplifyContourLines(contours[ci])
	}

	// Filter tiny fragments
	const validContours = contours.filter(c => curveRunArea(c) >= MIN_FRAGMENT_AREA)
	if (!validContours.length) return []

	// Separate contours into outers and holes. A contour whose start point is inside another contour is a hole of that contour.
	const outerContours = []
	const holeContours = []
	for (let i = 0; i < validContours.length; i++) {
		const c = validContours[i]
		const px = c[0][0],
			py = c[0][1]
		let isHole = false
		for (let j = 0; j < validContours.length; j++) {
			if (i === j) continue
			if (pointInBezierPath(px, py, validContours[j])) {
				isHole = true
				break
			}
		}
		if (isHole) holeContours.push(c)
		else outerContours.push(c)
	}

	// Reassemble: each outer contour becomes a piece, with its holes
	const pieces = []
	for (const contour of outerContours) {
		const outerSeg = curvesToSegment(contour)
		const segments = [outerSeg]
		if (holeContours.length > 0) {
			const fragPoly = flattenBezierPath([outerSeg])
			for (const holeCurves of holeContours) {
				const hx = holeCurves[0][0],
					hy = holeCurves[0][1]
				if (pointInPolygon(hx, hy, fragPoly)) {
					segments.push(curvesToSegment(holeCurves))
				}
			}
		}
		const pathStr = rawPathToString(segments)
		const r = resetPathOrigin(pathStr)
		pieces.push({ data: r.data, x: piece.x + r.x, y: piece.y + r.y })
	}

	return pieces.length ? pieces : []
}

/**
 * Erase from a variable-width brush by splitting its centerline. localEraserCurves: eraser curve8[][] in object-local space. Returns array of brush object props, or null if no intersection.
 */
export function eraseFromBrush(obj, localEraserCurves) {
	const centerlinePairs = sampleCenterline(obj.centerline)
	if (!centerlinePairs || centerlinePairs.length < 4) return null
	if (!localEraserCurves || !localEraserCurves.some(c => c && c.length >= 1)) return null

	// Flatten bezier contours to polygons for point-in-polygon tests
	const localEraserPoly = localEraserCurves.map(contour => flattenBezierPath([curvesToSegment(contour)]))

	const n = centerlinePairs.length / 2
	const sw = (obj.minWidth || 0) + (obj.widthGrowth ?? obj.maxWidth ?? obj.strokeWidth ?? 8)
	const cap = obj.strokeCap || 'round'
	const smoothness = obj.smoothness ?? 50
	const minW = obj.minWidth || 0

	// Compute arc lengths along the full stroke centerline for width mapping
	const baseHalfWidth = sw / 2
	const minHalfWidth = minW / 2
	const arcLengths = [0]
	for (let i = 1; i < n; i++) {
		const dx = centerlinePairs[i * 2] - centerlinePairs[(i - 1) * 2]
		const dy = centerlinePairs[i * 2 + 1] - centerlinePairs[(i - 1) * 2 + 1]
		arcLengths.push(arcLengths[i - 1] + Math.hypot(dx, dy))
	}
	const totalLength = arcLengths[n - 1] || 1
	const effectiveHW = totalLength < baseHalfWidth * 4 ? totalLength / 4 : baseHalfWidth

	// Point-in-polygon survival test
	const survived = new Uint8Array(n)
	let anyErased = false
	let anyKept = false

	for (let i = 0; i < n; i++) {
		const px = centerlinePairs[i * 2]
		const py = centerlinePairs[i * 2 + 1]
		if (localEraserPoly.some(ring => ring && pointInPolygon(px, py, ring))) {
			anyErased = true
		} else {
			survived[i] = 1
			anyKept = true
		}
	}

	if (!anyErased) return null // no intersection
	if (!anyKept) return [] // fully erased

	// Split into contiguous surviving runs, tracking original arc positions
	const runs = []
	let currentRun = null
	for (let i = 0; i < n; i++) {
		if (survived[i]) {
			if (!currentRun) currentRun = { pairs: [], startArc: arcLengths[i] }
			currentRun.pairs.push(centerlinePairs[i * 2], centerlinePairs[i * 2 + 1])
			currentRun.endArc = arcLengths[i]
		} else {
			if (currentRun && currentRun.pairs.length >= 4) runs.push(currentRun)
			currentRun = null
		}
	}
	if (currentRun && currentRun.pairs.length >= 4) runs.push(currentRun)

	if (runs.length === 0) return []

	const widthProfile = obj.widthProfile || 'diamond'
	const CAP_EPSILON = 2 // px — within this of original endpoints, keep original cap

	const results = []
	for (const run of runs) {
		const { startArc, endArc } = run

		// Determine caps: original ends keep obj.strokeCap, cut ends get 'butt'
		const capStart = startArc < CAP_EPSILON ? cap : 'butt'
		const capEnd = endArc > totalLength - CAP_EPSILON ? cap : 'butt'

		// hwAtFn maps the piece's local arc position back to the original stroke's arc, preserving the original width profile at the correct position
		const hwAtFn = (localArc, localTotal) => {
			const origT = (startArc + (localTotal > 0 ? localArc / localTotal : 0) * (endArc - startArc)) / totalLength
			let factor
			if (widthProfile === 'grow') factor = origT
			else if (widthProfile === 'shrink') factor = 1 - origT
			else factor = Math.sin(Math.PI * origT)
			return Math.max(minHalfWidth, minHalfWidth + (effectiveHW - minHalfWidth) * factor)
		}

		// Fit centerline bezier via Schneider (used for both outline and stored centerline)
		const clSegment = schneiderFit(run.pairs, smoothness)

		// Build outline using Tiller-Hanson pipeline — same as normal brushes
		const segments = buildOutlineFromBezier(
			clSegment,
			baseHalfWidth,
			minHalfWidth,
			cap,
			6,
			widthProfile,
			capStart,
			capEnd,
			hwAtFn
		)
		if (!segments || segments.length === 0) continue

		// Use first point as origin
		const ox = Math.round(run.pairs[0] * 10) / 10
		const oy = Math.round(run.pairs[1] * 10) / 10

		for (const seg of segments) {
			for (let i = 0; i < seg.length; i++) {
				seg[i] -= i % 2 ? oy : ox
			}
		}
		const data = rawPathToString(segments)

		const clShifted = clSegment.slice()
		for (let i = 0; i < clShifted.length; i++) {
			clShifted[i] -= i % 2 ? oy : ox
		}

		// Shift rawPoints to match the new origin
		const shiftedRun = new Array(run.pairs.length)
		for (let i = 0; i < run.pairs.length; i++) {
			shiftedRun[i] = run.pairs[i] - (i % 2 ? oy : ox)
		}

		const piece = {
			data,
			centerline: rawPathToString([clShifted]),
			rawPoints: shiftedRun.join(','),
			x: (obj.x || 0) + ox,
			y: (obj.y || 0) + oy,
		}

		// If only one run survived, mark as in-place update
		if (runs.length === 1) {
			piece._update = true
		}

		results.push(piece)
	}

	return results
}

/**
 * Erase from an image using mask-based approach. localEraserCurves: eraser curve8[][] in image-local space. Returns updated eraserMask string (JSON), or null if no intersection.
 */
export function eraseFromImage(obj, localEraserCurves) {
	const w = obj.width || 0
	const h = obj.height || 0
	if (w <= 0 || h <= 0) return null

	// Flatten bezier contours to polygons for polygon-clipping and mask storage
	const polyRings = localEraserCurves.map(contour => flattenBezierPath([curvesToSegment(contour)]))

	const imageRect = [
		[0, 0],
		[w, 0],
		[w, h],
		[0, h],
		[0, 0],
	]
	let intersection
	try {
		intersection = polygonClipping.intersection([imageRect], polyRings)
	} catch {
		return null
	}
	if (!intersection || intersection.length === 0) return null

	// Parse existing mask or start fresh
	let masks = []
	if (obj.eraserMask) {
		try {
			masks = JSON.parse(obj.eraserMask)
		} catch {
			masks = []
		}
	}

	for (const ring of polyRings) {
		masks.push(ring)
	}

	return JSON.stringify(masks)
}
